import './lists.css';

/**
 * No-results state for a history list (00-ds-09 §7, D148): when a search has no
 * match in the window, offer to search all time (matches may live in another
 * month). UI-only.
 */
export function ListEmpty({
  query,
  windowLabel,
  onSearchAllTime,
  message,
}: {
  /** The search term; omit for a plain "nothing here" state. */
  query?: string | undefined;
  /** The current window, e.g. "October 2026". */
  windowLabel?: string | undefined;
  /** Shown as "Search all time" when a search returned nothing in the window. */
  onSearchAllTime?: (() => void) | undefined;
  /** Override the headline entirely. */
  message?: React.ReactNode | undefined;
}) {
  const headline =
    message ??
    (query
      ? windowLabel
        ? `Nothing matches “${query}” in ${windowLabel}.`
        : `Nothing matches “${query}”.`
      : 'Nothing here yet.');
  return (
    <div className="lx-empty">
      <div className="lx-empty-title">{headline}</div>
      {onSearchAllTime ? (
        <div className="lx-empty-sub">
          <button type="button" className="lx-empty-link" onClick={onSearchAllTime}>
            Search all time
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Skeleton rows shown while more rows load (§7: never a full-page spinner). */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="lx-skel-row" />
      ))}
    </div>
  );
}

/**
 * A line shown when a search has matches outside the current window
 * (§3): "3 more in other months · Show all time".
 */
export function MoreInOtherMonths({ count, onShowAllTime }: { count: number; onShowAllTime: () => void }) {
  if (count <= 0) return null;
  return (
    <div className="lx-loadmore">
      <div className="lx-loadmore-count">
        {count.toLocaleString()} more in other months ·{' '}
        <button type="button" className="lx-empty-link" onClick={onShowAllTime}>
          Show all time
        </button>
      </div>
    </div>
  );
}
